export function formatCurrency(fils: number): string {
  return `JD ${(fils / 1000).toFixed(3)}`
}

// DrinX is a single-timezone business (Jordan), so every admin sees the SAME
// clock for the same event regardless of their machine's timezone. Jordan is
// a fixed UTC+3 (no DST since 2022); `timeZone: 'Asia/Amman'` stays correct
// even if that ever changes. Prefer these helpers over ad-hoc toLocaleString
// so timestamps don't render in the viewer's browser timezone (or raw UTC).
export const JORDAN_TZ = 'Asia/Amman'

// SQLite's `datetime('now')` returns "YYYY-MM-DD HH:MM:SS" with no timezone
// marker. JS Date parses those as LOCAL time, but they're actually UTC —
// which is why every timestamp drifts by the user's offset. Force-parse as
// UTC, then render in Jordan time below.
export function parseUtc(iso: string): Date {
  if (
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(iso) &&
    !/[+-]\d{2}:?\d{2}$|Z$/.test(iso)
  ) {
    return new Date(iso.replace(' ', 'T') + 'Z')
  }
  return new Date(iso)
}

export function formatDate(iso: string): string {
  return parseUtc(iso).toLocaleDateString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: JORDAN_TZ,
  })
}

export function formatDateTime(iso: string): string {
  return parseUtc(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: JORDAN_TZ,
  })
}

/// Compact label for a scheduled-delivery window — e.g. "Mon 7–8pm" or
/// "Today 19–20" depending on locale. Uses a 24h time format for
/// consistency with the driver app + dispatch UX.
export function formatScheduledWindow(iso: string, windowMinutes: number): string {
  const start = parseUtc(iso)
  if (Number.isNaN(start.getTime())) return ''
  const end = new Date(start.getTime() + windowMinutes * 60_000)
  // Day comparison + time rendering all in Jordan time (Asia/Amman) so the
  // window never drifts by the viewer's timezone.
  const ammanDay = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: JORDAN_TZ }) // YYYY-MM-DD
  const now = new Date()
  const tomorrow = new Date(now.getTime() + 86_400_000)
  const startDay = ammanDay(start)
  const datePart = startDay === ammanDay(now)
    ? 'Today'
    : startDay === ammanDay(tomorrow)
      ? 'Tomorrow'
      : start.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', timeZone: JORDAN_TZ })
  const fmt = (d: Date) =>
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: JORDAN_TZ })
  return `${datePart} ${fmt(start)}–${fmt(end)}`
}

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  // Derived status — backed by the returned_to_store_at marker column
  // on orders. See migration 0082 + the marker-vs-enum note in
  // backend/worker/src/routes/admin/orders.ts.
  returned_to_store: 'Returned to store',
}

export const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-500/15 text-yellow-400',
  confirmed: 'bg-blue-500/15 text-blue-400',
  preparing: 'bg-purple-500/15 text-purple-400',
  out_for_delivery: 'bg-orange-500/15 text-orange-400',
  delivered: 'bg-green-500/15 text-green-400',
  cancelled: 'bg-red-500/15 text-red-400',
  // Amber to distinguish from cancelled — a return is an operational
  // event with inventory recovery, not just a customer cancellation.
  returned_to_store: 'bg-amber-500/15 text-amber-400',
}

// Derive the effective status of an order from its row. Orders with a
// returned_to_store_at timestamp are surfaced as 'returned_to_store'
// even though their `status` column is 'cancelled' — see migration 0082
// for why this is a marker column rather than a new enum value.
export function effectiveOrderStatus(o: { status: string; returned_to_store_at?: string | null }): string {
  if (o.status === 'cancelled' && o.returned_to_store_at) return 'returned_to_store'
  return o.status
}

export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  operations: 'Operations',
  accounting: 'Accounting',
  marketing: 'Marketing',
}
